// frontend/src/pages/NewUITournamentDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import axios from '../config/axios';
import NewTournamentBracket from '../components/newui/NewTournamentBracket';
import NewReadyCheckModal from '../components/newui/NewReadyCheckModal';
import NewTeamInviteModal from '../components/newui/NewTeamInviteModal';
import { AlertCircle, ArrowLeft, UserPlus, UserMinus, CheckCircle, XCircle, Users } from 'lucide-react';
import NewUserAvatar from '../components/newui/NewUserAvatar';

const MaskIcon = ({ src, className = "w-5 h-5" }) => (
    <span
        aria-hidden="true"
        className={`inline-block ${className}`}
        style={{
            backgroundColor: "currentColor",
            WebkitMaskImage: `url(${src})`,
            maskImage: `url(${src})`,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            maskPosition: "center",
            WebkitMaskSize: "contain",
            maskSize: "contain",
        }}
    />
);

const PlayerList = ({ players }) => (
    <div className="bg-white/95 rounded-2xl p-6 shadow-lg">
        <h3 className="text-xl font-extrabold text-[#122436] uppercase tracking-wide mb-6 flex items-center gap-3">
            <MaskIcon src="/images/icons/users.png" className="w-6 h-6 text-[#2284DF]" />
            Jugadores Registrados
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {players.map(player => {
                const userForAvatar = {
                    username: player.user.username,
                    image: player.user.image,
                    name: player.user.name
                };

                return (
                    <div
                        key={player.userId}
                        className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl border-2 border-gray-200"
                    >
                        <NewUserAvatar user={userForAvatar} size="md" />
                        <div>
                            <p className="font-semibold text-[#122436]">{player.user.username}</p>
                            {player.isReady && (
                                <span className="text-xs text-green-600 font-bold">Listo</span>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    </div>
);

const TournamentStatus = ({ status, startTime }) => {
    const getStatusInfo = () => {
        switch (status) {
            case 'REGISTERING':
                return {
                    color: 'bg-[#fce503] text-[#122436]',
                    label: 'Registro Abierto'
                };
            case 'IN_PROGRESS':
                return {
                    color: 'bg-green-500 text-white',
                    label: 'En Curso'
                };
            default:
                return {
                    color: 'bg-gray-500 text-white',
                    label: 'Finalizado'
                };
        }
    };

    const info = getStatusInfo();

    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span className={`px-4 py-2 rounded-full text-sm font-extrabold uppercase ${info.color} inline-block w-fit`}>
                {info.label}
            </span>
            {status === 'REGISTERING' && (
                <span className="text-sm text-white/80 font-semibold">
                    Comienza {new Date(startTime).toLocaleString('es-AR', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                })}
                </span>
            )}
        </div>
    );
};

const NewUITournamentDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, updateUser } = useAuth();
    const { socket } = useSocket();

    const [tournament, setTournament] = useState(null);
    const [bracket, setBracket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [registering, setRegistering] = useState(false);
    const [teamInfo, setTeamInfo] = useState(null);
    const [pendingInvites, setPendingInvites] = useState([]);
    const [currentUserTeamId, setCurrentUserTeamId] = useState(null);
    const [teamName, setTeamName] = useState('');
    const [teamActionLoading, setTeamActionLoading] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);

    const [selectedMatch, setSelectedMatch] = useState(null);
    const [showReadyModal, setShowReadyModal] = useState(false);

    // Aplicar clase de fuente
    useEffect(() => {
        document.body.classList.add('acumin-scope');
        document.body.style.overflowX = 'hidden';
        return () => {
            document.body.classList.remove('acumin-scope');
            document.body.style.overflowX = '';
        };
    }, []);

    const fetchTournamentDetails = async () => {
        try {
            const [tournamentResponse, bracketResponse] = await Promise.all([
                axios.get(`/tournaments/${id}`),
                axios.get(`/matches/tournament/${id}/bracket`)
            ]);

            if (tournamentResponse.data.success) {
                setTournament(tournamentResponse.data.tournament);
                setTeamInfo(tournamentResponse.data.teamInfo || null);
                setPendingInvites(tournamentResponse.data.pendingInvites || []);
                setCurrentUserTeamId(tournamentResponse.data.currentUserTeamId || null);
            }

            if (bracketResponse.data.success) {
                const matches = Object.values(bracketResponse.data.bracket).flat();
                setBracket(matches);
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error al cargar el torneo');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTournamentDetails();
    }, [id]);

    useEffect(() => {
        if (!socket || !id) return;

        console.log('Setting up tournament socket listeners for tournament:', id);

        const handleTournamentUpdate = (data) => {
            console.log('Tournament update received:', data);

            if (data.tournament?.id === id) {
                setTournament(data.tournament);
                if (data.tournament?.teamSize > 1) {
                    fetchTournamentDetails();
                }

                if (data.type === 'PLAYER_REGISTERED' && data.newPlayer?.userId !== user.id) {
                    console.log(`Nuevo jugador registrado: ${data.newPlayer.user.username}`);
                } else if (data.type === 'PLAYER_UNREGISTERED') {
                    console.log('Un jugador abandonó el torneo');
                } else if (data.type === 'TOURNAMENT_STARTED') {
                    console.log('¡El torneo ha comenzado!');
                } else if (data.type === 'TOURNAMENT_COMPLETED') {
                    console.log(`¡Torneo terminado! Ganador: ${data.winner?.username || 'Desconocido'}`);
                }
            }
        };

        const handleBracketUpdate = (data) => {
            console.log('Bracket update received:', data);

            if (data.tournamentId === id) {
                setBracket(data.matches);
            }
        };

        const handleMatchReady = (data) => {
            console.log('Match ready received:', data);

            if (data.match?.tournamentId === id) {
                const isPlayerInMatch = data.match.player1Id === user.id || data.match.player2Id === user.id;
                if (isPlayerInMatch) {
                    setSelectedMatch(data.match);
                    setShowReadyModal(true);
                }
            }
        };

        socket.on('tournamentUpdate', handleTournamentUpdate);
        socket.on('bracketUpdate', handleBracketUpdate);
        socket.on('matchReady', handleMatchReady);

        return () => {
            console.log('Cleaning up tournament socket listeners');
            socket.off('tournamentUpdate', handleTournamentUpdate);
            socket.off('bracketUpdate', handleBracketUpdate);
            socket.off('matchReady', handleMatchReady);
        };
    }, [socket, id, user.id]);

    const handleGoBack = () => {
        if (location.state?.fromNotification) {
            console.log('📱 Navegación desde notificación - dirigiendo al home');
            navigate('/');
        } else {
            console.log('📱 Navegación normal - usando historial');
            navigate(-1);
        }
    };

    const isTeamTournament = tournament?.teamSize > 1;
    const isUserRegistered = isTeamTournament
        ? !!currentUserTeamId
        : tournament?.players.some(p => p.user.id === user.id);

    const handleRegister = async () => {
        if (!user.emailVerified) {
            setError('Necesitas verificar tu email para participar en torneos');
            return;
        }

        if (user.tickets < tournament.entryFee) {
            setError('No tienes suficientes tickets para participar');
            return;
        }

        setRegistering(true);
        setError(null);

        try {
            const response = await axios.post(`/tournaments/${id}/register`);
            if (response.data.success) {
                await fetchTournamentDetails();

                const newTicketAmount = user.tickets - tournament.entryFee;
                updateUser({ ...user, tickets: newTicketAmount });

                console.log(`Registrado en torneo. Tickets descontados: ${tournament.entryFee}. Nuevo saldo: ${newTicketAmount}`);
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error al registrarte en el torneo');
        } finally {
            setRegistering(false);
        }
    };

    const handleStartMatch = async (match) => {
        try {
            const response = await axios.post(`/matches/${match.id}/ready-check`);

            if (response.data.success) {
                setSelectedMatch(response.data.match);
                setShowReadyModal(true);
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error al iniciar el match');
        }
    };

    const handleCloseReadyModal = () => {
        setShowReadyModal(false);
        setSelectedMatch(null);
        fetchTournamentDetails();
    };

    const handleUnregister = async () => {
        setRegistering(true);
        setError(null);

        try {
            const response = await axios.post(`/tournaments/${id}/unregister`);
            if (response.data.success) {
                await fetchTournamentDetails();

                const newTicketAmount = user.tickets + tournament.entryFee;
                updateUser({ ...user, tickets: newTicketAmount });

                console.log(`Cancelado registro en torneo. Tickets devueltos: ${tournament.entryFee}. Nuevo saldo: ${newTicketAmount}`);
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error al salir del torneo');
        } finally {
            setRegistering(false);
        }
    };

    const handleCreateTeam = async () => {
        if (!user.emailVerified) {
            setError('Necesitas verificar tu email para participar en torneos');
            return;
        }

        if (!teamName.trim()) {
            setError('Necesitas ingresar un nombre de equipo');
            return;
        }

        if (user.tickets < tournament.entryFee) {
            setError('No tienes suficientes tickets para participar');
            return;
        }

        setTeamActionLoading(true);
        setError(null);

        try {
            const response = await axios.post(`/tournaments/${id}/teams`, {
                name: teamName.trim()
            });

            if (response.data.success) {
                setTeamName('');
                await fetchTournamentDetails();
                updateUser({ ...user, tickets: user.tickets - tournament.entryFee });
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error al crear el equipo');
        } finally {
            setTeamActionLoading(false);
        }
    };

    const handleJoinTeam = async (teamId) => {
        if (!user.emailVerified) {
            setError('Necesitas verificar tu email para participar en torneos');
            return;
        }

        if (user.tickets < tournament.entryFee) {
            setError('No tienes suficientes tickets para participar');
            return;
        }

        setTeamActionLoading(true);
        setError(null);

        try {
            const response = await axios.post(`/tournaments/${id}/teams/${teamId}/join`);
            if (response.data.success) {
                await fetchTournamentDetails();
                updateUser({ ...user, tickets: user.tickets - tournament.entryFee });
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error al unirte al equipo');
        } finally {
            setTeamActionLoading(false);
        }
    };

    const handleLeaveTeam = async () => {
        if (!currentUserTeam) return;

        setTeamActionLoading(true);
        setError(null);

        try {
            const response = await axios.post(`/tournaments/${id}/teams/${currentUserTeam.id}/leave`);
            if (response.data.success) {
                await fetchTournamentDetails();
                updateUser({ ...user, tickets: user.tickets + tournament.entryFee });
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error al salir del equipo');
        } finally {
            setTeamActionLoading(false);
        }
    };

    const handleDisbandTeam = async () => {
        if (!currentUserTeam) return;

        setTeamActionLoading(true);
        setError(null);

        try {
            const response = await axios.post(`/tournaments/${id}/teams/${currentUserTeam.id}/disband`);
            if (response.data.success) {
                await fetchTournamentDetails();
                updateUser({ ...user, tickets: user.tickets + tournament.entryFee });
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error al deshacer el equipo');
        } finally {
            setTeamActionLoading(false);
        }
    };

    const handleConfirmTeam = async () => {
        if (!currentUserTeam) return;

        setTeamActionLoading(true);
        setError(null);

        try {
            const response = await axios.post(`/tournaments/${id}/teams/${currentUserTeam.id}/confirm`);
            if (response.data.success) {
                await fetchTournamentDetails();
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error al confirmar el equipo');
        } finally {
            setTeamActionLoading(false);
        }
    };

    const handleSendInvite = async (teamId, invitedUserId) => {
        setTeamActionLoading(true);
        setError(null);

        try {
            const response = await axios.post(`/tournaments/${id}/teams/${teamId}/invite`, {
                invitedUserId
            });
            if (response.data.success) {
                await fetchTournamentDetails();
                return true;
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error al enviar la invitación');
            return false;
        } finally {
            setTeamActionLoading(false);
        }
    };

    const handleInviteResponse = async (inviteId, teamId, response) => {
        if (!user.emailVerified) {
            setError('Necesitas verificar tu email para participar en torneos');
            return;
        }

        setTeamActionLoading(true);
        setError(null);

        try {
            const apiResponse = await axios.post(`/tournaments/${id}/teams/${teamId}/invites/${inviteId}/respond`, {
                response
            });
            if (apiResponse.data.success) {
                await fetchTournamentDetails();
                if (response === 'ACCEPT') {
                    updateUser({ ...user, tickets: user.tickets - tournament.entryFee });
                }
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error al responder la invitación');
        } finally {
            setTeamActionLoading(false);
        }
    };

    const teamSize = tournament?.teamSize || 1;
    const currentUserTeam = isTeamTournament
        ? tournament?.teams?.find(team => team.id === currentUserTeamId)
        : null;
    const potentialTeams = isTeamTournament
        ? tournament?.teams?.filter(team => team.status === 'POTENTIAL') || []
        : [];
    const confirmedTeams = isTeamTournament
        ? tournament?.teams?.filter(team => team.status === 'CONFIRMED') || []
        : [];
    const isTeamCreator = currentUserTeam?.createdBy?.id === user.id;
    const canConfirmTeam = isTeamCreator &&
        currentUserTeam?.status === 'POTENTIAL' &&
        currentUserTeam?.members?.length === teamSize;
    const canInviteMembers = isTeamCreator && currentUserTeam?.status === 'POTENTIAL';
    const canDisbandTeam = isTeamCreator && tournament?.status === 'REGISTERING';
    const canLeaveTeam = currentUserTeam && !isTeamCreator && currentUserTeam.status === 'POTENTIAL';

    if (loading) {
        return (
            <div className="min-h-screen bg-[#122436] flex items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent"></div>
            </div>
        );
    }

    if (!tournament) {
        return (
            <div className="min-h-screen bg-[#122436] flex items-center justify-center p-4">
                <div className="text-center">
                    <h2 className="toon text-3xl text-white mb-4">Torneo no encontrado</h2>
                    <button
                        onClick={handleGoBack}
                        className="text-[#fce503] hover:text-[#fce503]/90 flex items-center gap-2 mx-auto font-bold"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Volver
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#122436] overflow-x-hidden">
            <div className="max-w-[calc(100%-100px)] mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
                <div className="space-y-8">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div>
                            <button
                                onClick={handleGoBack}
                                className="text-white/80 hover:text-white flex items-center gap-2 mb-4 font-semibold"
                            >
                                <ArrowLeft className="w-5 h-5" />
                                Volver
                            </button>
                            <h1 className="toon text-3xl md:text-4xl text-white mb-3 uppercase">
                                {tournament.name}
                            </h1>
                            <TournamentStatus
                                status={tournament.status}
                                startTime={tournament.startTime}
                            />
                        </div>

                        {tournament.status === 'REGISTERING' && !isTeamTournament && (
                            <div className="text-right">
                                {isUserRegistered ? (
                                    <button
                                        onClick={handleUnregister}
                                        disabled={registering}
                                        className="h-12 md:h-14 px-6 md:px-8 bg-red-500 text-white rounded-xl font-extrabold uppercase tracking-wide
                                                 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {registering ? 'Procesando...' : 'Abandonar Torneo'}
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleRegister}
                                        disabled={registering || tournament._count.players >= tournament.maxPlayers}
                                        className="h-12 md:h-14 px-6 md:px-8 bg-[#fce503] text-[#122436] rounded-xl font-extrabold uppercase tracking-wide
                                                 hover:bg-[#fce503]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors
                                                 border-2 border-[#fce503]"
                                    >
                                        {registering ? 'Procesando...' : 'Inscribirme'}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {error && (
                        <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4 flex items-center gap-3">
                            <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                            <p className="text-red-600 font-semibold">{error}</p>
                        </div>
                    )}

                    {/* Tournament Info */}
                    <div className="bg-white/95 rounded-2xl p-6 shadow-lg">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                            <div className="flex items-center gap-3">
                                <MaskIcon src="/images/icons/estrella.png" className="w-6 h-6 md:w-7 md:h-7 text-[#2284DF]" />
                                <div>
                                    <p className="text-sm text-gray-600 font-semibold">Entrada</p>
                                    <p className="font-extrabold text-[#122436]">{tournament.entryFee} tickets</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <MaskIcon src="/images/icons/billete.png" className="w-6 h-6 md:w-7 md:h-7 text-[#fce503]" />
                                <div>
                                    <p className="text-sm text-gray-600 font-semibold">Premio</p>
                                    <p className="font-extrabold text-[#122436]">{tournament.prizePool} tickets</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <MaskIcon src="/images/icons/users.png" className="w-6 h-6 md:w-7 md:h-7 text-[#2284DF]" />
                                <div>
                                    <p className="text-sm text-gray-600 font-semibold">
                                        {isTeamTournament ? 'Equipos Confirmados' : 'Jugadores'}
                                    </p>
                                    <p className="font-extrabold text-[#122436]">
                                        {isTeamTournament
                                            ? `${teamInfo?.confirmedTeamsCount || 0}/${teamInfo?.maxTeams || 0}`
                                            : `${tournament._count.players}/${tournament.maxPlayers}`
                                        }
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <MaskIcon src="/images/icons/reloj.png" className="w-6 h-6 md:w-7 md:h-7 text-gray-500" />
                                <div>
                                    <p className="text-sm text-gray-600 font-semibold">Inicio</p>
                                    <p className="font-extrabold text-[#122436] text-sm">
                                        {new Date(tournament.startTime).toLocaleString('es-AR', {
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Players / Teams */}
                    {isTeamTournament ? (
                        <div className="space-y-6">
                            {pendingInvites.length > 0 && (
                                <div className="bg-white/95 rounded-2xl p-6 shadow-lg">
                                    <h3 className="text-xl font-extrabold text-[#122436] uppercase tracking-wide mb-4 flex items-center gap-3">
                                        <UserPlus className="w-5 h-5 text-[#2284DF]" />
                                        Invitaciones Pendientes
                                    </h3>
                                    <div className="space-y-3">
                                        {pendingInvites.map(invite => (
                                            <div
                                                key={invite.id}
                                                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 rounded-xl border-2 border-gray-200 bg-gray-50"
                                            >
                                                <div>
                                                    <p className="font-bold text-[#122436]">{invite.team.name}</p>
                                                    <p className="text-xs text-gray-500">
                                                        Creador: {invite.team.createdBy?.username}
                                                    </p>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleInviteResponse(invite.id, invite.teamId, 'ACCEPT')}
                                                        disabled={teamActionLoading || currentUserTeamId}
                                                        className="h-10 px-4 bg-[#2284DF] text-white rounded-xl font-extrabold uppercase tracking-wide
                                                                 hover:bg-[#2A91F4] disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                                                    >
                                                        <CheckCircle className="w-4 h-4 inline-block mr-2" />
                                                        Aceptar
                                                    </button>
                                                    <button
                                                        onClick={() => handleInviteResponse(invite.id, invite.teamId, 'REJECT')}
                                                        disabled={teamActionLoading}
                                                        className="h-10 px-4 bg-gray-200 text-[#122436] rounded-xl font-extrabold uppercase tracking-wide
                                                                 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                                                    >
                                                        <XCircle className="w-4 h-4 inline-block mr-2" />
                                                        Rechazar
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {tournament.status === 'REGISTERING' && !currentUserTeam && (
                                <div className="bg-white/95 rounded-2xl p-6 shadow-lg">
                                    <h3 className="text-xl font-extrabold text-[#122436] uppercase tracking-wide mb-4 flex items-center gap-3">
                                        <Users className="w-5 h-5 text-[#2284DF]" />
                                        Crear Equipo
                                    </h3>
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <input
                                            type="text"
                                            value={teamName}
                                            onChange={(event) => setTeamName(event.target.value)}
                                            placeholder="Nombre del equipo"
                                            className="flex-1 h-12 rounded-xl border-2 border-gray-200 px-4 font-semibold text-[#122436]"
                                        />
                                        <button
                                            onClick={handleCreateTeam}
                                            disabled={teamActionLoading}
                                            className="h-12 px-6 bg-[#fce503] text-[#122436] rounded-xl font-extrabold uppercase tracking-wide
                                                     hover:bg-[#fce503]/90 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Crear Equipo
                                        </button>
                                    </div>
                                    <p className="mt-3 text-xs text-gray-500 font-semibold">
                                        Al crear o unirte a un equipo se reservan {tournament.entryFee} tickets por jugador.
                                    </p>
                                </div>
                            )}

                            {currentUserTeam && (
                                <div className="bg-white/95 rounded-2xl p-6 shadow-lg border-2 border-[#fce503]">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                                        <div>
                                            <h3 className="text-xl font-extrabold text-[#122436] uppercase tracking-wide">
                                                Mi Equipo
                                            </h3>
                                            <p className="text-sm text-gray-600 font-semibold">
                                                {currentUserTeam.name} · {currentUserTeam.status === 'CONFIRMED' ? 'Confirmado' : 'Potencial'}
                                            </p>
                                        </div>
                                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#122436] text-white">
                                            {currentUserTeam.members.length}/{teamSize}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {currentUserTeam.members.map(member => (
                                            <div
                                                key={member.userId}
                                                className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border-2 border-gray-200"
                                            >
                                                <NewUserAvatar user={member.user} size="md" />
                                                <div>
                                                    <p className="font-semibold text-[#122436]">{member.user.username}</p>
                                                    {member.userId === currentUserTeam.createdById && (
                                                        <span className="text-xs text-[#2284DF] font-bold">Creador</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-5 flex flex-wrap gap-3">
                                        {canInviteMembers && (
                                            <button
                                                onClick={() => setShowInviteModal(true)}
                                                disabled={teamActionLoading}
                                                className="h-11 px-5 bg-[#2284DF] text-white rounded-xl font-extrabold uppercase tracking-wide
                                                         hover:bg-[#2A91F4] disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                                            >
                                                <UserPlus className="w-4 h-4 inline-block mr-2" />
                                                Invitar
                                            </button>
                                        )}
                                        {canConfirmTeam && (
                                            <button
                                                onClick={handleConfirmTeam}
                                                disabled={teamActionLoading}
                                                className="h-11 px-5 bg-green-600 text-white rounded-xl font-extrabold uppercase tracking-wide
                                                         hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                                            >
                                                <CheckCircle className="w-4 h-4 inline-block mr-2" />
                                                Confirmar Equipo
                                            </button>
                                        )}
                                        {canLeaveTeam && (
                                            <button
                                                onClick={handleLeaveTeam}
                                                disabled={teamActionLoading}
                                                className="h-11 px-5 bg-gray-200 text-[#122436] rounded-xl font-extrabold uppercase tracking-wide
                                                         hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                                            >
                                                <UserMinus className="w-4 h-4 inline-block mr-2" />
                                                Salir
                                            </button>
                                        )}
                                        {canDisbandTeam && (
                                            <button
                                                onClick={handleDisbandTeam}
                                                disabled={teamActionLoading}
                                                className="h-11 px-5 bg-red-500 text-white rounded-xl font-extrabold uppercase tracking-wide
                                                         hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                                            >
                                                Deshacer Equipo
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="bg-white/95 rounded-2xl p-6 shadow-lg">
                                <h3 className="text-xl font-extrabold text-[#122436] uppercase tracking-wide mb-4 flex items-center gap-3">
                                    <Users className="w-5 h-5 text-[#2284DF]" />
                                    Equipos Potenciales
                                </h3>
                                {potentialTeams.length > 0 ? (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        {potentialTeams.map(team => {
                                            const isCurrentTeam = currentUserTeamId === team.id;
                                            const hasSlot = team.members.length < teamSize;

                                            return (
                                                <div
                                                    key={team.id}
                                                    className={`rounded-xl border-2 p-4 ${isCurrentTeam ? 'border-[#fce503] bg-[#fce503]/10' : 'border-gray-200 bg-gray-50'}`}
                                                >
                                                    <div className="flex items-start justify-between mb-3">
                                                        <div>
                                                            <p className="font-bold text-[#122436]">{team.name}</p>
                                                            <p className="text-xs text-gray-500">
                                                                Creador: {team.createdBy?.username}
                                                            </p>
                                                        </div>
                                                        <span className="text-xs font-bold bg-[#122436] text-white px-3 py-1 rounded-full">
                                                            {team.members.length}/{teamSize}
                                                        </span>
                                                    </div>

                                                    <div className="flex flex-wrap gap-2">
                                                        {team.members.map(member => (
                                                            <span
                                                                key={member.userId}
                                                                className="px-3 py-1 rounded-full bg-white border border-gray-200 text-xs font-semibold text-[#122436]"
                                                            >
                                                                {member.user.username}
                                                            </span>
                                                        ))}
                                                    </div>

                                                    <div className="mt-4">
                                                        {!currentUserTeam && tournament.status === 'REGISTERING' && hasSlot && (
                                                            <button
                                                                onClick={() => handleJoinTeam(team.id)}
                                                                disabled={teamActionLoading}
                                                                className="h-10 px-4 bg-[#fce503] text-[#122436] rounded-xl font-extrabold uppercase tracking-wide
                                                                         hover:bg-[#fce503]/90 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                                                            >
                                                                Unirme
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-600 font-semibold">
                                        No hay equipos potenciales por ahora.
                                    </p>
                                )}
                            </div>

                            {confirmedTeams.length > 0 && (
                                <div className="bg-white/95 rounded-2xl p-6 shadow-lg">
                                    <h3 className="text-xl font-extrabold text-[#122436] uppercase tracking-wide mb-4 flex items-center gap-3">
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                        Equipos Confirmados
                                    </h3>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        {confirmedTeams.map(team => (
                                            <div
                                                key={team.id}
                                                className="rounded-xl border-2 border-green-200 bg-green-50 p-4"
                                            >
                                                <p className="font-bold text-[#122436]">{team.name}</p>
                                                <p className="text-xs text-gray-500 mb-3">
                                                    Creador: {team.createdBy?.username}
                                                </p>
                                                <div className="flex flex-wrap gap-2">
                                                    {team.members.map(member => (
                                                        <span
                                                            key={member.userId}
                                                            className="px-3 py-1 rounded-full bg-white border border-green-200 text-xs font-semibold text-[#122436]"
                                                        >
                                                            {member.user.username}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <PlayerList players={tournament.players} />
                    )}

                    {/* Bracket */}
                    {tournament?.status !== 'REGISTERING' && bracket && (
                        <div className="overflow-x-auto">
                            <h2 className="toon text-3xl md:text-4xl text-white uppercase mb-6">
                                Bracket del Torneo
                            </h2>
                            <div className="min-w-[800px]">
                                <NewTournamentBracket
                                    matches={bracket}
                                    currentUserId={user.id}
                                    currentUserTeamId={currentUserTeamId}
                                    onStartMatch={handleStartMatch}
                                />
                            </div>
                        </div>
                    )}

                    {showReadyModal && selectedMatch && (
                        <NewReadyCheckModal
                            match={selectedMatch}
                            onClose={handleCloseReadyModal}
                        />
                    )}

                    {showInviteModal && currentUserTeam && (
                        <NewTeamInviteModal
                            isOpen={showInviteModal}
                            onClose={() => setShowInviteModal(false)}
                            tournamentId={id}
                            team={currentUserTeam}
                            onInvite={handleSendInvite}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewUITournamentDetail;
